import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, XCircle, Circle } from 'lucide-react';
import { checkinAPI, eventsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const RESULT = {
  ok: { Icon: CheckCircle2, cls: 'border-green-500 text-green-400', title: 'Checked in' },
  already_used: { Icon: AlertCircle, cls: 'border-yellow-500 text-yellow-400', title: 'Already checked in' },
  wrong_event: { Icon: XCircle, cls: 'border-red-500 text-red-400', title: 'Wrong event' },
  not_found: { Icon: XCircle, cls: 'border-red-500 text-red-400', title: 'Ticket not found' },
  unpaid: { Icon: XCircle, cls: 'border-red-500 text-red-400', title: 'Order not paid' },
  forbidden: { Icon: XCircle, cls: 'border-red-500 text-red-400', title: 'Not allowed' },
};

/**
 * Door check-in for the owner and anyone assigned with the check-in permission.
 * Works with USB/Bluetooth QR scanners (they type the code + Enter into the
 * focused field), manual entry, or tapping a guest in the list.
 */
export default function CheckInPage() {
  const { eventId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [data, setData] = useState(null);
  const [noList, setNoList] = useState(false);
  const [filter, setFilter] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(() => {
    checkinAPI.attendees(eventId).then(r => setData(r.data)).catch(e => { if (e?.response?.status === 403) setNoList(true); });
  }, [eventId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/auth?redirect=/check-in/${eventId}`); return; }
    eventsAPI.get(eventId).then(r => setTitle(r.data.title)).catch(() => {});
    load();
  }, [authLoading, user, eventId, load, navigate]);

  const submit = async (value) => {
    const v = (value ?? code).trim();
    if (!v) return;
    try {
      const r = await checkinAPI.scan(v, eventId);
      setResult({ ...r.data, result: 'ok' });
      setCode('');
      load();
    } catch (e) {
      const d = e?.response?.data || {};
      setResult({ result: d.result || 'not_found', detail: d.detail, ...(d.ticket || {}) });
    } finally {
      inputRef.current?.focus();
    }
  };

  const R = result ? RESULT[result.result] || RESULT.not_found : null;
  const q = filter.trim().toLowerCase();
  const tickets = (data?.tickets || []).filter(t => !q || (t.holder_name || '').toLowerCase().includes(q) || (t.holder_email || '').toLowerCase().includes(q) || t.ticket_id.toLowerCase().includes(q));

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-bold tracking-widest text-red-500 uppercase">Check-in</p>
            <h1 className="text-2xl font-black text-white uppercase">{title || '…'}</h1>
            <Link to={`/events/${eventId}`} className="text-sm text-gray-400 hover:text-red-400">Event page</Link>
          </div>
          {data && <p className="text-3xl font-black text-red-500">{data.checked_in}<span className="text-gray-500 text-lg">/{data.total}</span></p>}
        </div>

        <form onSubmit={e => { e.preventDefault(); submit(); }} className="flex gap-2">
          <input ref={inputRef} autoFocus value={code} onChange={e => setCode(e.target.value)} placeholder="Scan or type ticket code / ticket ID"
            className="flex-1 bg-[#111] border border-gray-700 text-white px-4 py-3 rounded focus:border-red-500 outline-none" />
          <button type="submit" className="btn-primary px-5 rounded font-black text-sm">CHECK IN</button>
        </form>

        {R && (
          <div className={`flex items-center gap-3 mt-4 p-4 rounded-lg border-2 bg-[#0A0A0A] ${R.cls}`}>
            <R.Icon size={32} />
            <div>
              <p className="font-black text-lg">{R.title}</p>
              {result.holder_name && <p className="text-white text-sm">{result.holder_name} · {result.ticket_type_name}</p>}
              {result.result !== 'ok' && result.detail && <p className="text-gray-300 text-sm">{result.detail}</p>}
            </div>
          </div>
        )}

        {noList ? (
          <p className="text-gray-500 text-sm text-center mt-8">You can check tickets in, but you don't have permission to see the guest list.</p>
        ) : (
          <>
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search guests"
              className="w-full bg-[#111] border border-gray-800 text-white px-4 py-2.5 rounded mt-6 mb-3 text-sm" />
            <div className="space-y-2">
              {data && tickets.length === 0 && <p className="text-gray-500 text-sm text-center">No tickets found.</p>}
              {tickets.map(t => (
                <button key={t.ticket_id} disabled={t.is_used}
                  onClick={() => window.confirm(`Check in ${t.holder_name || 'this guest'}?`) && submit(t.ticket_id)}
                  className="w-full flex items-center gap-3 bg-[#0A0A0A] border border-gray-800 rounded p-3 text-left hover:border-gray-600 disabled:hover:border-gray-800">
                  {t.is_used ? <CheckCircle2 size={18} className="text-green-400" /> : <Circle size={18} className="text-gray-600" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold">{t.holder_name || 'Guest'}</p>
                    <p className="text-gray-500 text-xs truncate">{t.ticket_type_name} · {t.ticket_id}</p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
